import Axios from '../../axiosConfig';
import { configure, makeAutoObservable, runInAction } from 'mobx';
import {
    ChapterIndicatorPackageDto,
    ChapterIndicatorThemeDto,
    DocumentIndicatorTopDto,
    GetIndicatorDataRequest,
    GraphDataItem,
    GraphDataTick,
    IndicatorChange,
    IndicatorDataDto,
    IndicatorDataOfOrganization,
    IndicatorDto,
    IndicatorTypeCode,
    OrganizationIndicatorData,
    OtherIndicatorSourceDto,
    SearchIndicatorRequest,
} from '../../types/indicator';
import { OrganizationDto } from '../../types/organization';
import RootModule from '../rootModule';
import { DocumentIndicatorSettingDto, EvaImpactOnTargetDto } from '../../types/document';
import { BACKEND_URL } from '../../config';

configure({ enforceActions: 'observed' });

export default class IndicatorModule {
    private rootModule: RootModule;
    public readonly COMPARE_ORGANIZATION_TO_OTHERS = 0 as const;
    public readonly COMPARE_OTHERS_TO_ORGANIZATION = 1 as const;
    public readonly changeLimits = { smallChangeMin: 5, mediumChangeMin: 10, largeChangeMin: 15 };

    private readonly compareYearLimits = {
        yearlyReportCompareYearLimit: 3,
        largeReportCompareYear: 5,
        largeReportCompareYearLimit: 3,
    };
    private indicatorViewMode = 'WIDE_MODE';
    private otherIndicatorSourceList: OtherIndicatorSourceDto[] = [];
    private indicatorTopList: DocumentIndicatorTopDto[] = [];

    private minimumDataContentIDsForArea: number[] = [];
    private minimumDataContentIDsForMunicipality: number[] = [];
    private settings: DocumentIndicatorSettingDto | null = null;
    private summaryDataForIndicatorTop: IndicatorDto[] | null = null;

    constructor(rootModule: RootModule) {
        makeAutoObservable(this, {}, { autoBind: true });
        this.rootModule = rootModule;
    }

    public get getMinimumDataContentIDsForArea(): number[] {
        return this.minimumDataContentIDsForArea;
    }

    public get getMinimumDataContentIDsForMunicipality(): number[] {
        return this.minimumDataContentIDsForMunicipality;
    }

    public changeIndicatorViewType(): void {
        this.indicatorViewMode === 'COMPACT_MODE'
            ? (this.indicatorViewMode = 'WIDE_MODE')
            : (this.indicatorViewMode = 'COMPACT_MODE');
    }

    public get getIndicatorViewMode(): string {
        return this.indicatorViewMode;
    }

    public get indicatorTops(): DocumentIndicatorTopDto[] {
        return this.indicatorTopList;
    }

    public get summaryData(): IndicatorDto[] | null {
        return this.summaryDataForIndicatorTop;
    }

    public set summaryData(data: IndicatorDto[] | null) {
        this.summaryDataForIndicatorTop = data;
    }

    public async fetchPackageThemes(packageId: number): Promise<void> {
        const documentId = this.rootModule.document.currentDocument.id;
        const indicatorChapterId = this.rootModule.document.indicatorPackageChapter?.id;

        const response = await Axios({
            method: 'GET',
            url:
                BACKEND_URL +
                `document/${documentId}/chapter/${indicatorChapterId}/chapterIndicator/listPackageThemes/${packageId}`,
            withCredentials: true,
        });
        const themes: ChapterIndicatorThemeDto[] = response.data;
        this.rootModule.document.setIndicatorPackageThemes(themes, packageId);
    }

    public async searchIndicators(source: string, text: string): Promise<IndicatorDto[]> {
        const organizationId = this.rootModule.organization.currentOrganizationId;
        const documentId = this.rootModule.document.currentDocument.id;
        const documentIsEva = this.rootModule.document.isCurrentDocumentEva;
        let chapterId = null;
        if (documentIsEva) {
            chapterId = this.rootModule.document.evaDecisionOptionChapter?.id;
        } else {
            chapterId = this.rootModule.document.indicatorPackageChapter?.id;
        }

        if (!chapterId) chapterId = this.rootModule.document.currentDocument.chapters[0]?.id;

        const request: SearchIndicatorRequest = {
            organizationId: organizationId!,
            source: source,
            text: text,
        };

        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + `document/${documentId}/chapter/${chapterId}/chapterIndicator/searchIndicator`,
            data: request,
            withCredentials: true,
        });
        return response.data;
    }

    public async fetchOtherIndicatorSources(): Promise<void> {
        const documentId = this.rootModule.document.currentDocument.id;
        let chapterId = this.rootModule.document.currentDocument.chapters.find(
            (chapter) => chapter.isIndicatorArea,
        )?.id;
        if (!chapterId) chapterId = this.rootModule.document.currentDocument.chapters[0].id;

        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + `document/${documentId}/chapter/${chapterId}/chapterIndicator/listOtherIndicatorSources`,
            withCredentials: true,
        });
        this.otherIndicatorSourceList = response.data as OtherIndicatorSourceDto[];
    }

    public async addIndicator(theme: ChapterIndicatorThemeDto): Promise<ChapterIndicatorThemeDto> {
        const documentId = this.rootModule.document.currentDocument.id;
        const chapterId = this.rootModule.document.indicatorPackageChapter?.id;
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + `document/${documentId}/chapter/${chapterId}/chapterIndicator/addIndicator`,
            data: theme,
            withCredentials: true,
        });
        // response.data is chapterIndicatorTheme with added/updated indicators.
        this.rootModule.document.addCurrentDocumentIndicators(response.data as ChapterIndicatorThemeDto);
        return response.data as ChapterIndicatorThemeDto;
    }

    public async create(indicator: IndicatorDto): Promise<void> {
        const documentId = this.rootModule.document.currentDocument.id;
        const indicatorChapterId = this.rootModule.document.indicatorPackageChapter?.id;

        const response = await Axios({
            method: 'POST',
            url:
                BACKEND_URL +
                `document/${documentId}/chapter/${indicatorChapterId}/chapterOrganizationIndicator/createOrganizationIndicator`,
            data: indicator,
            withCredentials: true,
        });

        const indicators = response.data as IndicatorDto[];

        const themes = this.rootModule.document.indicatorPackageChapter?.indicatorPackages.flatMap((pkg) => pkg.themes);

        runInAction(() => {
            indicators.forEach((indicator) => {
                const themeOfIndicator = themes?.find((t) => t.id === indicator.themeId);
                if (themeOfIndicator) {
                    const index = themeOfIndicator.indicators.findIndex((i) => i.id === indicator.id);

                    if (index !== -1) {
                        themeOfIndicator.indicators[index] = indicator;
                    } else {
                        themeOfIndicator.indicators.push(indicator);
                    }
                }
            });
        });
    }

    public async saveIndicator(indicator: IndicatorDto): Promise<void> {
        const documentId = this.rootModule.document.currentDocument.id;
        let indicatorChapterId = this.rootModule.document.indicatorPackageChapter?.id;
        if (!indicatorChapterId) indicatorChapterId = this.rootModule.document.currentDocument.chapters[0].id;

        if (indicator.sourceCode === 'CHP_SOTKANET') {
            await Axios({
                method: 'POST',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${indicatorChapterId}/chapterIndicator/updateSotkanetIndicator`,
                data: indicator,
                withCredentials: true,
            });
        } else if (indicator.sourceCode === 'CHP_CUSTOM') {
            await Axios({
                method: 'POST',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${indicatorChapterId}/chapterIndicator/updateOrganizationIndicator`,
                data: indicator,
                withCredentials: true,
            });
        } else if (indicator.sourceCode === 'CHP_OTHER') {
            await Axios({
                method: 'POST',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${indicatorChapterId}/chapterIndicator/updateOtherIndicator`,
                data: indicator,
                withCredentials: true,
            });
        }
    }

    public async fetchIndicatorData(params: GetIndicatorDataRequest, sourceCode: string): Promise<IndicatorDto> {
        const documentId = this.rootModule.document.currentDocument.id;
        let indicatorChapterId = this.rootModule.document.indicatorPackageChapter?.id;
        if (!indicatorChapterId) indicatorChapterId = this.rootModule.document.currentDocument.chapters[0].id;

        if (sourceCode === 'SOTKANET') {
            const response = await Axios({
                method: 'POST',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${indicatorChapterId}/chapterIndicator/getSotkanetIndicatorData`,
                data: params,
                withCredentials: true,
            });
            return response.data;
        } else if (sourceCode === 'CHP_SOTKANET') {
            const response = await Axios({
                method: 'POST',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${indicatorChapterId}/chapterIndicator/getChapterSotkanetIndicatorData`,
                data: params,
                withCredentials: true,
            });
            return response.data;
        } else if (sourceCode === 'CUSTOM') {
            const response = await Axios({
                method: 'POST',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${indicatorChapterId}/chapterIndicator/getOrganizationIndicatorData`,
                data: params,
                withCredentials: true,
            });
            return response.data;
        } else if (sourceCode === 'CHP_CUSTOM') {
            const response = await Axios({
                method: 'POST',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${indicatorChapterId}/chapterIndicator/getChapterOrganizationIndicatorData`,
                data: params,
                withCredentials: true,
            });
            return response.data;
        } else if (sourceCode === 'OTHER') {
            const response = await Axios({
                method: 'POST',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${indicatorChapterId}/chapterIndicator/getOtherIndicatorData`,
                data: params,
                withCredentials: true,
            });
            return response.data;
        } else {
            const response = await Axios({
                method: 'POST',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${indicatorChapterId}/chapterIndicator/getChapterOtherIndicatorData`,
                data: params,
                withCredentials: true,
            });
            return response.data;
        }
    }

    public async fetchIndicatorDataForPreview(indicator: IndicatorDto): Promise<IndicatorDto> {
        const documentId = this.rootModule.document.currentDocument.id;
        let chapterId = this.rootModule.document.indicatorPackageChapter?.id;
        if (!chapterId) chapterId = this.rootModule.document.currentDocument.chapters[0].id;

        if (indicator.sourceCode === 'SOTKANET') {
            const response = await Axios({
                method: 'GET',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${chapterId}/chapterIndicator/getSotkanetIndicatorDataForPreview/${indicator.id}`,
                withCredentials: true,
            });
            return response.data;
        } else if (indicator.sourceCode === 'CUSTOM') {
            const response = await Axios({
                method: 'GET',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${chapterId}/chapterIndicator/getOrganizationIndicatorDataForPreview/${indicator.id}`,
                withCredentials: true,
            });
            return response.data;
        } else {
            const response = await Axios({
                method: 'GET',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${chapterId}/chapterIndicator/getOtherIndicatorDataForPreview/${indicator.id}`,
                withCredentials: true,
            });
            return response.data;
        }
    }

    public async enableIndicator(indicator: IndicatorDto): Promise<void> {
        const documentId = this.rootModule.document.currentDocument.id;
        const chapterId = this.rootModule.document.currentDocument.chapters.find((chp) => chp.isIndicatorArea)?.id;

        if (indicator.sourceCode === 'CHP_SOTKANET') {
            await Axios({
                method: 'PUT',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${chapterId}/chapterIndicator/enableSotkanetIndicator/${indicator.id}`,
                withCredentials: true,
            });
        }

        if (indicator.sourceCode === 'CHP_CUSTOM') {
            await Axios({
                method: 'PUT',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${chapterId}/chapterIndicator/enableOrganizationIndicator/${indicator.id}`,
                withCredentials: true,
            });
        }

        if (indicator.sourceCode === 'CHP_OTHER') {
            await Axios({
                method: 'PUT',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${chapterId}/chapterIndicator/enableOtherIndicator/${indicator.id}`,
                withCredentials: true,
            });
        }
    }

    public async setPrintable(indicator: IndicatorDto): Promise<void> {
        const documentId = this.rootModule.document.currentDocument.id;
        const chapterId = this.rootModule.document.currentDocument.chapters.find((chp) => chp.isIndicatorArea)?.id;

        if (indicator.sourceCode === 'CHP_SOTKANET') {
            await Axios({
                method: 'PUT',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${chapterId}/chapterIndicator/setSotkanetIndicatorPrintable/${indicator.id}`,
                withCredentials: true,
            });
        }

        if (indicator.sourceCode === 'CHP_CUSTOM') {
            await Axios({
                method: 'PUT',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${chapterId}/chapterIndicator/setOrganizationIndicatorPrintable/${indicator.id}`,
                withCredentials: true,
            });
        }

        if (indicator.sourceCode === 'CHP_OTHER') {
            await Axios({
                method: 'PUT',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${chapterId}/chapterIndicator/setOtherIndicatorPrintable/${indicator.id}`,
                withCredentials: true,
            });
        }
    }

    public async fetchSummary(chapterId: number): Promise<string | null> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + `document/getSummary/${chapterId}`,
            withCredentials: true,
        });
        if (response.status === 200) return response.data;
        else return null;
    }

    public async fetchDataForIndicatorTop(documentId: number, chapterId: number): Promise<IndicatorDto[]> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + `document/${documentId}/chapter/${chapterId}/chapterIndicator/getDataForIndicatorTop`,
            withCredentials: true,
        });
        return response.data as IndicatorDto[];
    }

    public async fetchIndicatorTops(): Promise<void> {
        const documentId = this.rootModule.document.currentDocument.id;
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + `documentIndicator/getIndicatorTops/${documentId}`,
            withCredentials: true,
        });
        runInAction(() => {
            this.indicatorTopList = response.data;
        });
    }

    public async addIndicatorTop(indicator: IndicatorDto): Promise<void> {
        const documentIndicatorTop: DocumentIndicatorTopDto = {
            id: null,
            documentId: this.rootModule.document.currentDocument.id!,
            themeId: indicator.themeId!,
            indicatorId: indicator.id!,
            sourceCode: indicator.sourceCode,
        };
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + `documentIndicator/addIndicatorTop`,
            data: documentIndicatorTop,
            withCredentials: true,
        });

        const responseData: DocumentIndicatorTopDto = response.data;

        runInAction(() => {
            this.indicatorTopList.push(responseData);
            this.fetchSummaryDataForIndicatorTop();
        });
    }

    public async removeIndicatorTop(indicator: IndicatorDto): Promise<void> {
        const documentIndicatorTopId = this.indicatorTopList.find(
            (indicatorTop) =>
                indicatorTop.indicatorId === indicator.indicatorSourceId &&
                indicatorTop.themeId === indicator.themeId &&
                indicatorTop.documentId === this.rootModule.document.currentDocument.id,
        )?.id;

        if (documentIndicatorTopId) {
            const response = await Axios({
                method: 'DELETE',
                url: BACKEND_URL + `documentIndicator/removeIndicatorTop/${documentIndicatorTopId}`,
                withCredentials: true,
            });
            runInAction(() => {
                const removedIndicatorTopId: number = response.data;
                this.indicatorTopList = this.indicatorTopList.filter(
                    (indicatorTop) => indicatorTop.id !== removedIndicatorTopId,
                );
                this.fetchSummaryDataForIndicatorTop();
            });
        }
    }

    public async fetchSummaryDataForIndicatorTop(): Promise<void> {
        const summaryChapter = this.rootModule.document.currentDocument.chapters.find((chp) => chp.isSummary);
        if (summaryChapter) {
            const data = await this.fetchDataForIndicatorTop(summaryChapter.documentId, summaryChapter.id!);
            runInAction(() => {
                this.summaryDataForIndicatorTop = data;
            });
        }
    }

    public async saveIndicatorDirection(indicator: IndicatorDto): Promise<void> {
        const documentId = this.rootModule.document.currentDocument.id;
        const chapterId = this.rootModule.document.currentDocument.chapters.find((chp) => chp.isIndicatorArea)!.id;

        if (indicator.sourceCode === 'CHP_SOTKANET') {
            await Axios({
                method: 'POST',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${chapterId}/chapterIndicator/updateSotkanetIndicatorDirection`,
                data: indicator,
                withCredentials: true,
            });
        } else if (indicator.sourceCode === 'CHP_CUSTOM') {
            await Axios({
                method: 'POST',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${chapterId}/chapterIndicator/updateOrganizationIndicatorDirection`,
                data: indicator,
                withCredentials: true,
            });
        } else {
            await Axios({
                method: 'POST',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${chapterId}/chapterIndicator/updateOtherIndicatorDirection`,
                data: indicator,
                withCredentials: true,
            });
        }
    }

    public async removeIndicator(indicator: IndicatorDto): Promise<void> {
        const documentId = this.rootModule.document.currentDocument.id;
        const chapterId = this.rootModule.document.currentDocument.chapters.find((chp) => chp.isIndicatorArea)!.id;

        if (indicator.sourceCode === 'CHP_SOTKANET') {
            await Axios({
                method: 'DELETE',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${chapterId}/chapterIndicator/removeSotkanetIndicator/${indicator.id}`,
                withCredentials: true,
            });
        } else if (indicator.sourceCode === 'CHP_CUSTOM') {
            await Axios({
                method: 'DELETE',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${chapterId}/chapterIndicator/removeOrganizationIndicator/${indicator.id}`,
                withCredentials: true,
            });
        } else {
            await Axios({
                method: 'DELETE',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${chapterId}/chapterIndicator/removeOtherIndicator/${indicator.id}`,
                withCredentials: true,
            });
        }
        this.rootModule.document.removeCurrentDocumentIndicator(indicator);
    }

    public async removeIndicatorFromImpactOnTarget(
        impactOnTarget: EvaImpactOnTargetDto,
        indicator: IndicatorDto,
    ): Promise<void> {
        const documentId = this.rootModule.document.currentDocument.id;
        const chapterId = this.rootModule.document.evaDecisionOptionChapter?.id;

        if (indicator.sourceCode === 'SOTKANET') {
            await Axios({
                method: 'DELETE',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${chapterId}/chapterEva/removeEvaImpactOnTargetSotkanetIndicator/evaImpactOnTargetId/${impactOnTarget.id}/indicatorId/${indicator.id}`,
                withCredentials: true,
            });
        } else {
            await Axios({
                method: 'DELETE',
                url:
                    BACKEND_URL +
                    `document/${documentId}/chapter/${chapterId}/chapterEva/removeEvaImpactOnTargetOrganizationIndicator/evaImpactOnTargetId/${impactOnTarget.id}/indicatorId/${indicator.id}`,
                withCredentials: true,
            });
        }
        this.rootModule.document.removeCurrentDocumentImpactOnTargetIndicator(impactOnTarget, indicator);
    }

    public async addImpactOnTargetIndicator(params: EvaImpactOnTargetDto): Promise<void> {
        const documentId = this.rootModule.document.currentDocument.id;
        const chapterId = this.rootModule.document.evaDecisionOptionChapter?.id;

        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + `document/${documentId}/chapter/${chapterId}/chapterEva/addEvaImpactOnTargetIndicator`,
            data: params,
            withCredentials: true,
        });
        this.rootModule.document.addCurrentDocumentImpactOnTargetIndicators(response.data as EvaImpactOnTargetDto);
    }

    public async saveComparisonSettings(settings: DocumentIndicatorSettingDto): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'document/saveComparisonSettings',
            data: settings,
            withCredentials: true,
        });

        runInAction(() => {
            this.settings = response.data as DocumentIndicatorSettingDto;
        });
    }

    public async fetchMinimumDataContentIDsForArea(): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + `minimumDataContents/listIndicatorIdsForAreas`,
            withCredentials: true,
        });
        runInAction(() => {
            this.minimumDataContentIDsForArea = response.data;
        });
    }

    public async fetchMinimumDataContentIDsForMunicipality(): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + `minimumDataContents/listIndicatorIdsForMunicipalities`,
            withCredentials: true,
        });
        runInAction(() => {
            this.minimumDataContentIDsForMunicipality = response.data;
        });
    }

    public clearComparisonSettings(): void {
        this.settings = null;
    }

    public setComparisonSettings(comparisonSettings: DocumentIndicatorSettingDto): void {
        if (!comparisonSettings.compareDirection) {
            comparisonSettings.compareDirection = this.COMPARE_ORGANIZATION_TO_OTHERS;
        }
        this.settings = comparisonSettings;
    }

    public get otherIndicatorSources(): OtherIndicatorSourceDto[] {
        return this.otherIndicatorSourceList;
    }

    public get comparisonSettings(): DocumentIndicatorSettingDto | null {
        return this.settings;
    }

    public indicatorPackageById(id: number): ChapterIndicatorPackageDto | null {
        return this.rootModule.document.indicatorPackageChapter?.indicatorPackages.find((pkg) => pkg.id === id) || null;
    }

    public indicatorsByTypeFromTheme = (
        indicatorTheme: ChapterIndicatorThemeDto,
        code: IndicatorTypeCode,
    ): IndicatorDto[] => {
        return indicatorTheme.indicators.filter((indicator) => indicator.type === code);
    };

    public sortIndicatorsByEnabled(indicators: IndicatorDto[]): IndicatorDto[] {
        // Sort the indicators first by orderNumber, and then by their enabled status. Enabled indicators
        // appear before !enabled indicators.
        const sortedByOrder = this.sortByOrderNumber(indicators);

        return sortedByOrder.sort((a, b) => {
            if (a.enabled && !b.enabled) return -1;
            else if (!a.enabled && b.enabled) return 1;
            else {
                if (a.orderNumber === null) return 1;
                if (b.orderNumber === null) return -1;
                return a.orderNumber - b.orderNumber;
            }
        });
    }

    public sortByOrderNumber(indicators: IndicatorDto[]): IndicatorDto[] {
        return indicators.sort((a, b) => {
            if (a.orderNumber === null) return 1;
            if (b.orderNumber === null) return -1;
            return a.orderNumber - b.orderNumber;
        });
    }

    public removeDuplicatesFromIndicatorData(data: IndicatorDataDto[]): IndicatorDataDto[] {
        return data.reduce((acc, cur) => {
            if (!acc.some((item) => item.year === cur.year && item.organization.id === cur.organization.id)) {
                acc.push(cur);
            }
            return acc;
        }, [] as IndicatorDataDto[]);
    }

    public filterIndicatorDataBetweenCompareYears = (data: IndicatorDataDto[]): IndicatorDataDto[] => {
        if (!this.comparisonSettings) return data;
        const { startYear, endYear } = this.comparisonSettings;
        if (!startYear || !endYear) return data;
        return data.filter((item) => item.year >= startYear && item.year <= endYear);
    };

    public groupIndicatorDataByOrganization(data: IndicatorDataDto[]): OrganizationIndicatorData[] {
        const organizationData: OrganizationIndicatorData[] = [];

        data.forEach((dataItem) => {
            const inIndicatorTops = dataItem.inIndicatorTops;
            const organization = organizationData.find(
                (orgItem) => orgItem.organization.id === dataItem.organization.id,
            );
            if (!organization) {
                organizationData.push({
                    organization: dataItem.organization,
                    data: [dataItem],
                    inIndicatorTops: inIndicatorTops,
                });
            } else {
                organization.data.push(dataItem);
            }
        });

        return organizationData;
    }

    public groupIndicatorDataByOrganizationNew(data: IndicatorDataDto[]): IndicatorDataOfOrganization[] {
        const organizationData: IndicatorDataOfOrganization[] = [];

        data.forEach((dataItem) => {
            const org = dataItem.organization;
            const organization = organizationData.find((orgItem) => orgItem.organization.id === org.id);
            if (!organization) {
                organizationData.push({
                    organization: org,
                    data: [dataItem],
                    inIndicatorTops: dataItem.inIndicatorTops,
                });
            } else {
                organization.data.push(dataItem);
            }
        });

        return organizationData;
    }

    public calculateChange = (data: IndicatorDataDto[] | null): IndicatorChange => {
        const numericChange: IndicatorChange = { value: null, startYear: null, endYear: null };

        if (!data) return numericChange;
        if (data.length < 2) return numericChange;

        const reportType = this.rootModule.document.currentDocument.documentType.code;
        const lastItem = data[data.length - 1];

        if (reportType === 'LARGE_REPORT' || reportType === 'REGIONAL_LARGE_REPORT') {
            const result = this.getLargeReportCompareYear(lastItem.year, data);
            if (result.compareItem) {
                numericChange.value = this.calculateChangePercent(result.compareItem.value!, lastItem.value!);
                numericChange.startYear = result.compareItem?.year || null;
                numericChange.endYear = lastItem.year;
            }
        } else if (
            reportType === 'YEARLY_REPORT' ||
            reportType === 'REGIONAL_YEARLY_REPORT' ||
            reportType === 'ANOTHER_REPORT'
        ) {
            const compareItem = data[data.length - 2];
            if (lastItem.year - compareItem.year <= this.compareYearLimits.yearlyReportCompareYearLimit) {
                numericChange.value = this.calculateChangePercent(compareItem.value!, lastItem.value!);
                numericChange.startYear = compareItem?.year || null;
                numericChange.endYear = lastItem.year;
            }
        }

        return numericChange;
    };

    private calculateChangePercent = (oldValue: number, newValue: number): number => {
        if (oldValue === 0) {
            if (newValue > 0) return 100;
            else if (newValue < 0) return -100;
            else return 0;
        }
        return ((newValue - oldValue) / Math.abs(oldValue)) * 100;
    };

    private getLargeReportCompareYear = (
        year: number,
        data: IndicatorDataDto[],
    ): { compareItem: IndicatorDataDto | null; fetchData: boolean | null } => {
        const { largeReportCompareYear, largeReportCompareYearLimit } = this.compareYearLimits;

        for (const item of data) {
            if (year - item.year === largeReportCompareYear) {
                return { compareItem: item, fetchData: false };
            } else if (year - item.year >= largeReportCompareYearLimit && year - item.year <= largeReportCompareYear) {
                return { compareItem: item, fetchData: true };
            }
        }

        return { compareItem: null, fetchData: null };
    };

    public filterCurrentOrganizationData = (
        data: IndicatorDataDto[],
        origoOrganization: OrganizationDto | null,
    ): IndicatorDataDto[] => {
        const { currentDocument } = this.rootModule.document;
        const organization = origoOrganization || currentDocument.organization;
        return data.filter((indicatorData) => indicatorData.organization.id === organization.id);
    };

    public dataForLargeGraph(graphData: GraphDataItem[]): GraphDataTick[] {
        const years = new Set(graphData.flatMap((data) => data.values.flatMap((value) => value.year)));
        const data = [...years].map((year) => ({
            year: year,
        }));
        data.forEach((data) => {
            graphData.forEach((organization) => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                data[organization.shortName || organization.name] =
                    organization.values.find((o) => o.year === data.year)?.value ?? null;
            });
        });
        return data.sort((a, b) => b.year - a.year);
    }

    public dynamicMarginTopForGraphLabel(organizationsForGraph: { id: number; name: string }[]): number {
        let margin;
        const organizationCount = organizationsForGraph.length;
        if (organizationCount <= 10) {
            margin = 40;
        } else if (organizationCount > 10 && organizationCount <= 15) {
            margin = 50;
        } else if (organizationCount > 15 && organizationCount <= 20) {
            margin = 60;
        } else if (organizationCount > 20 && organizationCount <= 25) {
            margin = 70;
        } else if (organizationCount > 25 && organizationCount <= 30) {
            margin = 80;
        } else margin = 90;

        return margin;
    }
}
